import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import Footer from "../SharedComponents/Footer";
import PlacesSection from "../SharedComponents/PlaceSection";
import Navbar from "./Navbar";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface City {
    id: number;
    name: string;
    place_count: number;
    description?: string;
    image?: string;
}

const CITY_SECTIONS = [
    { title: (city: string) => `جاذبه‌های دیدنی ${city}`, category: "جاذبه‌های دیدنی" },
    { title: (city: string) => `رستوران‌های ${city}`, category: "رستوران" },
    { title: (city: string) => `کافه‌های ${city}`, category: "کافه" },
    { title: (city: string) => `اقامتگاه‌های ${city}`, category: "اقامتگاه" },
];

const CityPage: React.FC = () => {
    const { city } = useParams<{ city: string }>();
    const decodedCity = decodeURIComponent(city ?? "");

    const { data: cityData, isLoading: cityLoading } = useQuery<City[], Error, City | undefined>({
        queryKey: ["cities"],
        queryFn: async () => {
            const res = await fetch(`${BASE_URL}/api/cities/`);
            if (!res.ok) throw new Error("خطا در دریافت شهرها");
            const json = await res.json();
            return Array.isArray(json) ? json : json.data || [];
        },
        select: (cities) => cities.find((c) => c.name === decodedCity),
        staleTime: 10 * 60 * 1000,
    });

    const imageUrl = cityData?.image
        ? cityData.image.startsWith("http")
            ? cityData.image
            : `${BASE_URL}${cityData.image}`
        : null;

    const hasImage = !!imageUrl;

    return (
        <div className="min-h-screen min-w-screen">
            {/* Navbar is fixed — floats over the hero */}
            <Navbar />

            {/* ── Hero — starts at top-0, slides behind fixed navbar ── */}
            <div className="relative w-full h-72 mt-14 md:h-80 lg:h-96 overflow-hidden">

                {/* Background */}
                {cityLoading ? (
                    <div className="w-full h-full bg-gray-200 animate-pulse" />
                ) : hasImage ? (
                    <img
                        src={imageUrl!}
                        alt={decodedCity}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <div className="w-full h-full bg-gray-100" />
                )}

                {/* Gradient — only with image */}
                {hasImage && !cityLoading && (
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/20" />
                )}

                {/* ── Breadcrumb — sits just below the fixed navbar ── */}
                {!cityLoading && (
                    <div className="absolute top-4 inset-x-0 px-[16px] tablet:px-20 desktop:px-36">
                        <nav className="flex items-center gap-1.5 text-sm font-myYekanFaNumRegular">
                            <Link
                                to="/"
                                className={`transition ${hasImage ? "text-white/90 hover:text-white" : "text-gray-800 hover:text-gray-700"}`}
                            >
                                خانه
                            </Link>
                            <span className={hasImage ? "text-white/90" : "text-gray-800"}>&raquo;</span>
                            <Link
                                to="/destinations"
                                className={`transition ${hasImage ? "text-white/90 hover:text-white" : "text-gray-800 hover:text-gray-700"}`}
                            >
                                شهرها
                            </Link>
                            <span className={hasImage ? "text-white/90" : "text-gray-800"}>&raquo;</span>
                            <span className={`font-myYekanDemibold ${hasImage ? "text-white" : "text-gray-800"}`}>
                                {decodedCity}
                            </span>
                        </nav>
                    </div>
                )}

                {/* ── City name — anchored to bottom of hero ── */}
                {!cityLoading && (
                    <div className="absolute bottom-0 inset-x-0 px-[16px] tablet:px-20 desktop:px-36 pb-6">
                        <h1 className={`text-2xl md:text-3xl lg:text-4xl font-myYekanDemibold ${hasImage ? "text-white" : "text-gray-900"}`}>
                            {decodedCity}
                        </h1>
                        {!!cityData?.place_count && (
                            <span className={`text-sm font-myYekanFaNumRegular mt-1 block ${hasImage ? "text-white/55" : "text-gray-400"}`}>
                                {cityData.place_count} مکان
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* ── Page body ── */}
            <div className="max-w-8xl mx-auto">

                {/* Description */}
                {cityData?.description && (
                    <div className="pt-4 px-0 desktop:px-16">
                        <div className="desktop:px-20 tablet:px-20 px-[16px] pt-5 pb-1">
                            <div className="prose prose-sm md:prose-base max-w-none text-justify
                                text-gray-500 font-myYekanFaNumRegular leading-7
                                prose-headings:font-myYekanDemibold prose-headings:text-gray-800
                                prose-strong:text-gray-700 prose-strong:font-myYekanDemibold
                                prose-a:text-green-600 prose-a:no-underline hover:prose-a:underline
                                prose-li:marker:text-gray-400">
                                <ReactMarkdown>{cityData.description}</ReactMarkdown>
                            </div>
                        </div>
                    </div>
                )}

                <div className="mt-8">
                    {/* Place sections */}
                    {CITY_SECTIONS.map(({ title, category }) => (
                        <PlacesSection
                            key={category}
                            title={title(decodedCity)}
                            category={category}
                            city={decodedCity}
                            hideIfEmpty
                        />
                    ))}
                </div>


            </div>

            <Footer />
        </div>
    );
};

export default CityPage;